import React, { useEffect } from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { PURCHASE_EVENT_STATUS, SCRIPT_TAG_STATUS } from '@enums';
import { useModal } from '@hooks';
import { Typography } from '@ui';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import { useScriptTag } from '../../../../../../../components/IntegrationKlickly/hooks/useScriptTag';
import { SETUP_KEYS } from '../constants';
import AnalyticsScriptModal from '../modals/AnalyticsScriptModal';
import ApiSetupModal from '../modals/ApiSetupModal';
import CustomersModal from '../modals/CustomersModal';
import OrdersModal from '../modals/OrdersModal';
import ProductCatalogModal from '../modals/ProductCatalogModal';
import PurchaseEventsModal from '../modals/PurchaseEventsModal';
import Footer from '../shared/Footer';
import Title from '../shared/Title';

export const useSetupModal = (store, data, activeKey, key) => {
    const { open, close, update, modal } = useModal();
    const { hasRequiredParams } = useScriptTag();

    const {
        setupInfo,
        setupHistorical,
        setupPurchase,
        purchaseSetupPending = false,
        setupScriptTag,
        historicalSetupOrdersPending = false,
        historicalSetupCustomersPending = false,
        scriptTagSetupPending = false
    } = store.accountsStore;

    const onClose = () => {
        close();
    };

    const MODAL_CONFIG = {
        'api-setup': {
            title: <Title dataE2e='api' text='API setup' onClick={() => window.open('/docs/api', '_blank')} />,
            content: <ApiSetupModal data={data} />,
            footer: <Footer showStatus={false} showConfirm={false} close={onClose} />
        },
        [SETUP_KEYS.ANALYTICS_SCRIPT]: {
            title: (
                <Title
                    dataE2e='script'
                    isManual={data.isManual}
                    text='Analytics script setup'
                    onClick={() => window.open('/docs/api', '_blank')}
                />
            ),
            content: <AnalyticsScriptModal data={data} />,
            footer: (
                <Footer
                    close={onClose}
                    status={data.status}
                    confirmDisable={!hasRequiredParams}
                    confirm={() => {
                        close();
                        setupScriptTag();
                    }}
                    fetching={scriptTagSetupPending}
                    pending={data.status === SCRIPT_TAG_STATUS.PENDING}
                    error={
                        setupInfo.scriptTag.lastPingAt && data.status === SCRIPT_TAG_STATUS.NOT_CONNECTED ? (
                            <Flex data-e2e='integrations-script-modal_error_text' vertical style={BOTTOM_INDENTS.M}>
                                <Typography.Text type='badgeSmall' color={COLORS.$tartRed}>
                                    Script check failed at{' '}
                                    {dayjs(setupInfo.scriptTag.lastPingAt).format('DD MMM YYYY, HH:mm:ss')}
                                </Typography.Text>
                                <Typography.Text type='badgeSmall' color={COLORS.$tartRed}>
                                    We were unable to confirm the script in your Store
                                </Typography.Text>
                            </Flex>
                        ) : null
                    }
                />
            )
        },
        [SETUP_KEYS.PURCHASE_EVENT]: {
            title: (
                <Title
                    dataE2e='purchase'
                    isManual={data.isManual}
                    text='Purchase events setup'
                    onClick={() => window.open('/docs/api', '_blank')}
                />
            ),
            content: <PurchaseEventsModal data={data} />,
            footer: (
                <Footer
                    key={data.status}
                    fetching={purchaseSetupPending}
                    pending={data.status === PURCHASE_EVENT_STATUS.PENDING}
                    confirm={() => {
                        close();
                        setupPurchase();
                    }}
                    close={onClose}
                    status={data.status}
                    error={
                        setupInfo.scriptTag.lastPingPurchaseAt &&
                        data.status === PURCHASE_EVENT_STATUS.NOT_CONNECTED ? (
                            <Flex data-e2e='integrations-purchase-modal_error_text' vertical style={BOTTOM_INDENTS.M}>
                                <Typography.Text type='badgeSmall' color={COLORS.$tartRed}>
                                    Script check failed at{' '}
                                    {dayjs(setupInfo.scriptTag.lastPingPurchaseAt).format('DD MMM YYYY, HH:mm:ss')}
                                </Typography.Text>
                                <Typography.Text type='badgeSmall' color={COLORS.$tartRed}>
                                    We were unable to confirm the script on your Thank You Page
                                </Typography.Text>
                            </Flex>
                        ) : null
                    }
                />
            )
        },
        'product-catalog': {
            title: (
                <Title
                    dataE2e='product-catalog'
                    text='Product catalog setup'
                    onClick={() => window.open('/docs/api#tag/products', '_blank')}
                />
            ),
            content: <ProductCatalogModal data={data} />,
            footer: <Footer close={onClose} status={data.status} showConfirm={false} />
        },
        orders: {
            title: (
                <Title
                    dataE2e='orders'
                    text='Orders setup'
                    onClick={() => window.open('/docs/api#tag/webhook_orders', '_blank')}
                />
            ),
            content: <OrdersModal data={data} />,
            footer: (
                <Footer
                    close={onClose}
                    status={data.status}
                    confirm={() => {
                        close();
                        setupHistorical('orders');
                    }}
                    pending={historicalSetupOrdersPending}
                />
            )
        },
        customers: {
            title: (
                <Title
                    dataE2e='customers'
                    text='Customers setup'
                    onClick={() => window.open('/docs/api#tag/webhook_customers', '_blank')}
                />
            ),
            content: <CustomersModal data={data} />,
            footer: (
                <Footer
                    close={onClose}
                    status={data.status}
                    pending={historicalSetupCustomersPending}
                    confirm={() => {
                        close();
                        setupHistorical('customers');
                    }}
                />
            )
        }
    };

    const openModal = (key) => {
        if (!MODAL_CONFIG[key]) {
            return;
        }
        open({
            ...MODAL_CONFIG[key],
            settings: {
                width: '630px',
                footer: MODAL_CONFIG[key].footer
            }
        });
    };

    // Use update to dynamically update the modal when `data.status` changes
    useEffect(() => {
        if (activeKey === key && Boolean(modal) && MODAL_CONFIG[key]) {
            update({
                settings: {
                    footer: MODAL_CONFIG[key].footer
                }
            });
        }
    }, [
        data.status,
        purchaseSetupPending,
        scriptTagSetupPending,
        historicalSetupOrdersPending,
        historicalSetupCustomersPending
    ]);

    return { openModal, close };
};
